import React, { useState } from 'react';
import { CheckCircle2, PauseCircle, Clock, XCircle } from 'lucide-react';
import type { PackageStatus, PromotedListingCover, RiskLevel } from '@/client/types/promotion';
import { RISK_META } from './listingMetrics';

export const numberFmt = new Intl.NumberFormat('en-US');

export const initials = (s: string) =>
  s.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';

const STATUS_STYLE: Record<PackageStatus, { badge: string; icon: React.ElementType }> = {
  active: { badge: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', icon: CheckCircle2 },
  scheduled: { badge: 'bg-blue-50 text-blue-700 ring-blue-600/20', icon: Clock },
  paused: { badge: 'bg-amber-50 text-amber-700 ring-amber-600/20', icon: PauseCircle },
  expired: { badge: 'bg-gray-100 text-gray-600 ring-gray-500/20', icon: XCircle },
  canceled: { badge: 'bg-rose-50 text-rose-700 ring-rose-600/20', icon: XCircle },
};

export const StatusBadge: React.FC<{ status: PackageStatus }> = ({ status }) => {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.expired;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${s.badge}`}>
      <Icon className="h-3 w-3" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

export const RiskBadge: React.FC<{ risk: RiskLevel; showBlurb?: boolean }> = ({ risk, showBlurb }) => {
  const m = RISK_META[risk] ?? RISK_META.low;
  return (
    <span
      title={m.blurb}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${m.badge}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {showBlurb ? m.label : m.label.replace(' risk', '')}
    </span>
  );
};

/** Cover thumbnail, falling back to initials when there's no asset or it 404s. */
export const ListingThumb: React.FC<{
  cover: PromotedListingCover | null | undefined;
  name: string;
  className?: string;
}> = ({ cover, name, className = 'h-9 w-9 rounded-lg text-[11px]' }) => {
  const [failed, setFailed] = useState(false);
  const src = cover?.public_url || cover?.public_url_uc;

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        className={`shrink-0 bg-gray-100 object-cover ${className}`}
      />
    );
  }
  return (
    <span
      className={`flex shrink-0 items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 font-semibold text-white ${className}`}
    >
      {initials(name)}
    </span>
  );
};

export const StatTile: React.FC<{ icon: React.ElementType; label: string; value: string; tone: string }> = ({
  icon: Icon,
  label,
  value,
  tone,
}) => (
  <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3">
    <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${tone}`}>
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      <p className="text-lg font-semibold text-gray-900">{value}</p>
      <p className="truncate text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
    </div>
  </div>
);

export const ProgressBar: React.FC<{ value: number; target: number }> = ({ value, target }) => {
  const pct = target > 0 ? Math.min(100, Math.round((value / target) * 100)) : 0;
  const color = pct >= 90 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500">{pct}%</span>
    </div>
  );
};
